import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import css from './components/DietPlanBuilder.module.css';
import { BuilderTopBar, MobileNav, ShellFooter, type BuildStep } from './components/BuilderShell';
import { TemplatePicker } from './components/steps/TemplatePicker';
import { GoalsStep } from './components/steps/GoalsStep';
import { MealTargetsStep } from './components/steps/MealTargetsStep';
import { MealsStep } from './components/steps/MealsStep';
import { MealSplitSheet } from './components/sheets/MealSplitSheet';
import { OptionEditorSheet } from './components/sheets/OptionEditorSheet';
import { FoodSearchSheet } from './components/sheets/FoodSearchSheet';
import { DiscardSheet } from './components/sheets/AssignSheet';
import { PreviewAssignSheet } from './components/sheets/PreviewAssignSheet';
import { copy } from './copy';
import { dietPlanApi } from './api/dietPlanApi';
import { useDietPlanBuilder } from './hooks/useDietPlanBuilder';
import { useIsMobile } from './hooks/useMediaQuery';
import { useAsync } from './hooks/useAsync';
import { firstName, fmtDate, fmtInt, hasValidDateRange, hasValidPlanDateRange, plusDaysIso, todayIso } from './lib/format';
import { gramsPerDay } from './lib/nutrition';
import { planFromDietPlanApi, planFromTemplate, PROGRAM_TAG } from './lib/plan';
import {
  defaultPlanRange,
  findOverlap,
  nextFreeRange,
  occupiedRanges,
  PLAN_DURATION_DAYS,
  type OccupiedRange,
} from './lib/planDates';
import { getDietPlanWithOldKeys } from './lib/payload';
import { uploadDietPlanRecord } from '../../../utils/uploadDietPlanRecord';
import { AppDispatch, RootState } from '../../../store/store';
import type { Patient, Template } from './types';
import { resolveSaveTemplate } from './types';
import { assignStatusForSave } from './assignStatus';

const AssessmentSideDrawer = React.lazy(
  () => import('../AssessmentSideDrawer/AssessmentSideDrawer'),
);

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
  /** Called after a successful draft save (builder stays open). */
  onDraftSaved?: () => void;
  /** Existing pending/draft plan. Opens preview & assign with this plan loaded. */
  editDietPlan?: any;
  /** Source plan for copy. Same load path as edit, but saves as a new plan. */
  copyDietPlan?: any;
};

const DietPlanBuilder: React.FC<Props> = ({ onCancel, onSuccess, onDraftSaved, editDietPlan, copyDietPlan }) => {
  const dispatchRedux = useDispatch<AppDispatch>();
  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const { healthCoachDetails } = useSelector((state: RootState) => state.healthCoach);
  const pd = patientDetails?.data;
  const coach = healthCoachDetails?.data;
  const patientId = pd?.patient_id || null;

  const patient: Patient = useMemo(
    () => ({
      id: pd?.patient_id || '',
      name: pd?.name || 'Patient',
      age: Number(pd?.patient_age) || 0,
      sex: pd?.gender || '',
      program: pd?.disease || pd?.medical_condition_name || '',
      programName: pd?.medical_condition_name || pd?.disease || '',
    }),
    [pd],
  );

  const isMobile = useIsMobile();
  const builder = useDietPlanBuilder(patientId, dietPlanApi);
  const { s, dispatch, api, derived, kaira } = builder;
  const plan = s.plan;
  const first = firstName(patient.name);
  const coachName = [coach?.first_name, coach?.last_name].filter(Boolean).join(' ') || 'Coach';

  const templates = useAsync(() => dietPlanApi.listTemplates(), []);
  const [pickedTpl, setPickedTpl] = useState<Template | null>(null);
  const [tplLoading, setTplLoading] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [occupied, setOccupied] = useState<OccupiedRange[]>([]);
  const [occupiedLoad, setOccupiedLoad] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const overlayRef = useRef<HTMLDivElement>(null);
  const editBooted = useRef(false);
  const [editPlanStatus, setEditPlanStatus] = useState<string | null>(null);

  useLayoutEffect(() => {
    const source = editDietPlan || copyDietPlan;
    if (!source || editBooted.current) return;
    editBooted.current = true;
    const legacy = getDietPlanWithOldKeys(source);
    const next = planFromDietPlanApi(source, first);
    if (editDietPlan) {
      const id = editDietPlan.diet_plans_id || legacy?.diet_plan_id;
      if (id) next.id = id;
      const st = editDietPlan.status ?? legacy?.status;
      setEditPlanStatus(st != null && st !== '' ? String(st) : null);
    }
    dispatch({
      type: 'startPlan',
      plan: next,
      tplName: legacy?.diet_name || source.plan_name || '',
      screen: 'meals',
      sheet: 'previewAssign',
    });
  }, [editDietPlan, copyDietPlan, first, dispatch]);

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const root = document.getElementById('root');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('gf-diet-builder-open');
    root?.setAttribute('inert', '');
    root?.setAttribute('aria-hidden', 'true');
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.classList.remove('gf-diet-builder-open');
      root?.removeAttribute('inert');
      root?.removeAttribute('aria-hidden');
      const el = overlayRef.current;
      if (el) {
        el.style.width = '';
        el.style.left = '';
      }
    };
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const sync = () => {
      const el = overlayRef.current;
      if (!el) return;
      const kb = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
      el.style.setProperty('--kb', `${kb}px`);
      if (isMobile && vv) {
        el.style.width = `${vv.width}px`;
        el.style.left = `${vv.offsetLeft}px`;
      } else {
        el.style.width = '';
        el.style.left = '';
      }
    };
    sync();
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [isMobile]);

  const flash = useCallback((m: string) => {
    setFlashMsg(m);
    window.setTimeout(() => setFlashMsg(null), 1800);
  }, []);

  const closeSheet = () => dispatch({ type: 'sheet', sheet: null });

  useEffect(() => {
    if (!patientId) {
      setOccupied([]);
      setOccupiedLoad('idle');
      return;
    }
    let cancelled = false;
    setOccupiedLoad('loading');
    dietPlanApi
      .listPlans(String(patientId))
      .then((plans) => {
        if (cancelled) return;
        setOccupied(occupiedRanges(plans));
        setOccupiedLoad('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOccupied([]);
        setOccupiedLoad('error');
        const message =
          err instanceof Error ? err.message : copy.goals.occupiedLoadFailed;
        toast.error(message);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const retryOccupiedPlans = useCallback(() => {
    if (!patientId || occupiedLoad === 'loading') return;
    setOccupiedLoad('loading');
    dietPlanApi
      .listPlans(String(patientId))
      .then((plans) => {
        setOccupied(occupiedRanges(plans));
        setOccupiedLoad('ready');
      })
      .catch((err: unknown) => {
        setOccupied([]);
        setOccupiedLoad('error');
        const message =
          err instanceof Error ? err.message : copy.goals.occupiedLoadFailed;
        toast.error(message);
      });
  }, [patientId, occupiedLoad]);

  const occupancyReady = occupiedLoad === 'ready';

  const blockingOccupied = useMemo(
    () => occupied.filter((r) => !plan?.id || String(r.id) !== String(plan.id)),
    [occupied, plan?.id],
  );
  const dateOverlap = plan ? findOverlap(plan.from, plan.to, blockingOccupied) : null;
  const suggestedRange = useMemo(
    () => nextFreeRange(blockingOccupied, todayIso(), PLAN_DURATION_DAYS, plusDaysIso),
    [blockingOccupied],
  );

  useEffect(() => {
    if (!plan || plan.id != null || !blockingOccupied.length || !dateOverlap) return;
    const fallback = defaultPlanRange(todayIso(), PLAN_DURATION_DAYS, plusDaysIso);
    if (plan.from !== fallback.from || plan.to !== fallback.to) return;
    dispatch({ type: 'plan', fn: p => { p.from = suggestedRange.from; p.to = suggestedRange.to; } });
  }, [blockingOccupied, dateOverlap, dispatch, plan, suggestedRange.from, suggestedRange.to]);

  const applySuggestedDates = useCallback(() => {
    dispatch({ type: 'plan', fn: p => { p.from = suggestedRange.from; p.to = suggestedRange.to; } });
  }, [dispatch, suggestedRange.from, suggestedRange.to]);

  const onClose = () => {
    if (s.sheet) {
      closeSheet();
      return;
    }
    onCancel();
  };

  const onFooterBack = () => {
    if (s.screen === 'goals') {
      if (editDietPlan) {
        onCancel();
        return;
      }
      if (plan) dispatch({ type: 'sheet', sheet: 'leave' });
      else dispatch({ type: 'discard' });
      return;
    }
    if (isMobile) {
      if (s.screen === 'meals') dispatch({ type: 'go', screen: 'goals' });
      else if (s.screen === 'targets') dispatch({ type: 'go', screen: 'goals' });
      return;
    }
    if (s.screen === 'targets') dispatch({ type: 'go', screen: 'goals' });
    if (s.screen === 'meals') dispatch({ type: 'go', screen: 'targets' });
  };

  useEffect(() => {
    if (isMobile && s.screen === 'targets') dispatch({ type: 'go', screen: 'meals' });
  }, [isMobile, s.screen, dispatch]);

  const pickTemplate = async (t: Template | null) => {
    if (!occupancyReady) {
      toast.error(
        occupiedLoad === 'loading'
          ? copy.goals.occupiedLoading
          : copy.goals.occupiedLoadFailed,
      );
      return;
    }
    const dates = nextFreeRange(occupied, todayIso(), PLAN_DURATION_DAYS, plusDaysIso);
    if (!t) {
      setPickedTpl(null);
      dispatch({ type: 'startPlan', plan: planFromTemplate(null, first, dates), tplName: '' });
      return;
    }
    try {
      setTplLoading(true);
      const full = await api.getTemplate(t.id);
      setPickedTpl(full);
      dispatch({ type: 'startPlan', plan: planFromTemplate(full, first, dates), tplName: full.name });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load template');
    } finally {
      setTplLoading(false);
    }
  };

  const assignStatus = (asDraft: boolean) =>
    assignStatusForSave(asDraft, {
      isChief: coach?.chief_hc === 'Y',
      editingPlanId: plan?.id,
      loadedStatus: editPlanStatus,
    });

  const persist = async (asDraft: boolean): Promise<boolean> => {
    if (!plan || !patientId) return false;
    if (!occupancyReady) {
      toast.error(
        occupiedLoad === 'loading'
          ? copy.goals.occupiedLoading
          : copy.goals.occupiedLoadFailed,
      );
      return false;
    }
    if (dateOverlap) {
      toast.error(copy.goals.overlap(dateOverlap.name, fmtDate(dateOverlap.from), fmtDate(dateOverlap.to)));
      return false;
    }
    if (!hasValidPlanDateRange(plan.from, plan.to, plan.id)) {
      toast.error(
        plan.id == null && plan.from < todayIso()
          ? copy.goals.pastStart
          : copy.goals.badDates,
      );
      return false;
    }
    const status = assignStatus(asDraft);
    if (asDraft) setDraftStatus('saving');
    let data: Awaited<ReturnType<typeof api.savePlan>>;
    try {
      data = await api.savePlan(plan, status, String(patientId));
    } catch {
      setDraftStatus('error');
      toast.error('Could not save diet plan');
      return false;
    }
    if (Number(data?.code) === 0) {
      setDraftStatus('error');
      toast.error(data?.message || 'Could not save diet plan');
      return false;
    }
    if (Number(data?.code) === 1) {
      const created = data?.data;
      if (created?.diet_plans_id || created?.diet_plan_id) {
        dispatch({ type: 'markSaved', id: created.diet_plans_id || created.diet_plan_id });
      } else {
        dispatch({ type: 'markSaved' });
      }
      try {
        await uploadDietPlanRecord(dispatchRedux, {
          planStatus: created?.plan_status,
          apiResponse: data,
          dietPlanData: created,
          fallbackPatientId: patientId,
          userDetails: coach,
        });
      } catch {
        toast.error('Failed to upload diet plan to patient records');
      }
      setDraftStatus('saved');
      if (!asDraft) {
        const saveTpl = resolveSaveTemplate(plan, s.saveTplMode, s.newTplName);
        if (saveTpl) {
          try {
            const tplRes = await api.saveTemplate(plan, saveTpl);
            if (Number(tplRes?.code) === 1) {
              toast.success(
                saveTpl.kind === 'master'
                  ? copy.assign.tplSavedMaster
                  : copy.assign.tplSavedCopy(saveTpl.name),
              );
            } else {
              toast.warn(tplRes?.message || copy.assign.tplSaveFailed);
            }
          } catch {
            toast.warn(copy.assign.tplSaveFailed);
          }
        }
        toast.success(data?.message || 'Diet plan assigned');
        onSuccess();
      } else {
        flash(copy.draft.saved);
        onDraftSaved?.();
      }
      return true;
    }
    setDraftStatus('error');
    toast.error(data?.message || 'Could not save diet plan');
    return false;
  };

  const onAutofill = (dietPlanData: any) => {
    const legacy = getDietPlanWithOldKeys(dietPlanData);
    const next = planFromDietPlanApi(dietPlanData, first);
    if (occupancyReady && findOverlap(next.from, next.to, occupied)) {
      const dates = nextFreeRange(occupied, todayIso(), PLAN_DURATION_DAYS, plusDaysIso);
      next.from = dates.from;
      next.to = dates.to;
    }
    dispatch({
      type: 'startPlan',
      plan: next,
      tplName: legacy?.diet_name || '',
    });
    setShowAssessment(false);
  };

  const buildStep: BuildStep | null =
    s.screen === 'goals' ? 'goals' : s.screen === 'targets' ? 'targets' : s.screen === 'meals' ? 'meals' : null;

  const onTopStep = (step: BuildStep) => {
    if (!plan) return;
    const order: BuildStep[] = ['goals', 'targets', 'meals'];
    const cur = buildStep ? order.indexOf(buildStep) : s.sheet === 'previewAssign' ? 3 : -1;
    const next = order.indexOf(step);
    if (next <= cur) dispatch({ type: 'go', screen: step });
  };

  const goalsDatesOk = plan
    ? hasValidPlanDateRange(plan.from, plan.to, plan.id) && !dateOverlap && occupancyReady
    : false;
  const goalsPctSum = plan ? plan.pct.p + plan.pct.c + plan.pct.f : 0;
  const goalsPctOk = goalsPctSum === 100;

  const footer = ((): { text: string; tone: 'ok' | 'warn' | 'muted' } => {
    if (draftStatus === 'saved') return { text: copy.draft.saved, tone: 'ok' };
    if (draftStatus === 'error') return { text: copy.draft.failed, tone: 'warn' };
    if (s.screen === 'goals' && plan && !occupancyReady) {
      return {
        text:
          occupiedLoad === 'loading'
            ? copy.goals.occupiedLoading
            : copy.goals.occupiedLoadFailed,
        tone: 'warn',
      };
    }
    if (s.screen === 'goals' && plan && dateOverlap) {
      return {
        text: copy.goals.overlap(dateOverlap.name, fmtDate(dateOverlap.from), fmtDate(dateOverlap.to)),
        tone: 'warn',
      };
    }
    if (
      s.screen === 'goals'
      && plan
      && plan.id == null
      && /^\d{4}-\d{2}-\d{2}$/.test(plan.from)
      && plan.from < todayIso()
    ) {
      return { text: copy.goals.pastStart, tone: 'warn' };
    }
    if (s.screen === 'goals' && plan && !hasValidDateRange(plan.from, plan.to)) {
      return { text: copy.goals.badDates, tone: 'warn' };
    }
    if (s.screen === 'goals' && plan && !goalsPctOk) {
      return { text: `Macro split adds up to ${goalsPctSum}%. It needs to be 100%.`, tone: 'warn' };
    }
    if (s.screen === 'goals' && plan) {
      const gp = gramsPerDay(plan.kcal, plan.pct, 'p');
      const gc = gramsPerDay(plan.kcal, plan.pct, 'c');
      const gf = gramsPerDay(plan.kcal, plan.pct, 'f');
      return {
        text: `Daily goal: ${fmtInt(plan.kcal)} kcal · P ${gp} g · C ${gc} g · F ${gf} g`,
        tone: 'ok',
      };
    }
    if (s.screen === 'targets' && derived) {
      if (derived.shareSum === 100) return { text: copy.targets.sumOk(plan!.meals.filter(m => !m.skip).length), tone: 'ok' };
      return { text: copy.targets.sumOff(derived.shareSum), tone: 'warn' };
    }
    if (s.screen === 'meals' && derived?.tipsOverLimit) return { text: copy.meals.tipsOverLimitFooter, tone: 'warn' };
    if (s.screen === 'meals' && derived && derived.mealsWithoutFood > 0) {
      return { text: copy.meals.mealsMissingFoodFooter, tone: 'warn' };
    }
    if (s.screen === 'meals' && derived?.offOpts) return { text: copy.meals.off(derived.offOpts), tone: 'warn' };
    return { text: 'All changes save when you assign or save a draft.', tone: 'muted' };
  })();

  const assessmentDrawer = showAssessment ? (
    <div
      className={css.drawerScrim}
      role="presentation"
      onClick={() => setShowAssessment(false)}
    >
      <div className={css.drawerPanel} role="dialog" aria-label="Assessment" onClick={e => e.stopPropagation()}>
        <Suspense fallback={<div className={css.center}>…</div>}>
          <AssessmentSideDrawer onAutofill={onAutofill} onCloseToIndex={() => setShowAssessment(false)} />
        </Suspense>
      </div>
    </div>
  ) : null;

  const node = (
    <div className={css.overlay} ref={overlayRef} data-mobile={isMobile || undefined}>
      <div className={css.splitRoot}>
        <div className={`${css.root} ${css.builderCol}`}>
          {isMobile ? (
            <MobileNav
              title={
                s.screen === 'tpl' ? copy.titles.tpl
                  : s.screen === 'goals' ? copy.titles.goals
                  : s.screen === 'targets' ? copy.titles.targets
                  : copy.titles.meals
              }
              sub={
                s.screen === 'tpl' ? copy.subs.tpl
                  : s.screen === 'goals' ? copy.subsMobile.goals
                  : copy.subsMobile.meals
              }
              steps={3}
              step={
                s.screen === 'tpl' ? -1
                  : s.screen === 'goals' ? 0
                  : s.screen === 'meals' ? 1
                  : -1
              }
              onBack={s.screen === 'tpl' ? onClose : onFooterBack}
            />
          ) : (
            <BuilderTopBar
              mode={s.screen === 'tpl' ? 'choose' : 'build'}
              activeStep={buildStep}
              plan={plan}
              onStep={onTopStep}
              onAssessment={() => setShowAssessment(true)}
              onClose={onClose}
              mealsReviewCount={derived?.offOpts}
              flowPastMeals={s.sheet === 'previewAssign'}
            />
          )}

          <div className={css.scrollMain}>
            {s.screen === 'tpl' && (
              <TemplatePicker
                templates={templates.data}
                loading={templates.loading || tplLoading}
                filter={s.tplFilter}
                recommendedTag={PROGRAM_TAG[String(patient.program).toLowerCase()] || null}
                onFilter={(v) => dispatch({ type: 'setFilter', v })}
                onPick={pickTemplate}
              />
            )}
            {s.screen === 'goals' && plan && (
              <GoalsStep plan={plan} dispatch={dispatch} occupied={blockingOccupied} />
            )}
            {s.screen === 'targets' && plan && derived && !isMobile && (
              <MealTargetsStep
                plan={plan}
                derived={derived}
                tpl={pickedTpl}
                dispatch={dispatch}
                onEditGoals={() => dispatch({ type: 'go', screen: 'goals' })}
              />
            )}
            {s.screen === 'meals' && plan && derived && (
              <MealsStep plan={plan} patient={patient} derived={derived} mi={s.mi} oi={s.oi} targetEdit={s.targetEdit} dispatch={dispatch} api={api} kaira={kaira} />
            )}
          </div>

          {s.screen !== 'tpl' && plan && (
            <ShellFooter
              statusText={footer.text}
              statusTone={footer.tone}
              canBack={!isMobile}
              onBack={!isMobile ? onFooterBack : undefined}
              onDraft={!isMobile && occupancyReady ? () => { void persist(true); } : undefined}
              draftBusy={draftStatus === 'saving'}
              primaryLabel={
                isMobile
                  ? s.screen === 'goals'
                    ? copy.goals.continue
                    : copy.meals.previewAs
                  : s.screen === 'goals'
                  ? copy.goals.next
                  : s.screen === 'targets'
                  ? copy.targets.next
                  : copy.meals.next
              }
              onPrimary={() => {
                if (s.screen === 'goals') {
                  dispatch({ type: 'go', screen: isMobile ? 'meals' : 'targets' });
                } else if (s.screen === 'targets') dispatch({ type: 'go', screen: 'meals' });
                else dispatch({ type: 'sheet', sheet: 'previewAssign' });
              }}
              primaryDisabled={
                !occupancyReady
                || (s.screen === 'goals' && (!goalsDatesOk || !goalsPctOk))
                || (s.screen === 'meals'
                  && (!!derived?.tipsOverLimit || (derived?.mealsWithoutFood ?? 0) > 0))
              }
            />
          )}

          {s.sheet === 'split' && plan && (
            <MealSplitSheet plan={plan} tpl={pickedTpl} state={s} dispatch={dispatch} onClose={closeSheet} />
          )}
          {s.sheet === 'opt' && plan && (
            <OptionEditorSheet
              plan={plan}
              patient={patient}
              state={s}
              dispatch={dispatch}
              kaira={kaira}
              onClose={closeSheet}
              flash={flash}
            />
          )}
          {s.sheet === 'search' && plan && (
            <FoodSearchSheet plan={plan} state={s} dispatch={dispatch} api={api} onClose={closeSheet} />
          )}
          {s.sheet === 'previewAssign' && plan && derived && (
            <PreviewAssignSheet
              plan={plan}
              derived={derived}
              state={s}
              dispatch={dispatch}
              onAssign={() => persist(false)}
              onClose={closeSheet}
              overlap={dateOverlap}
              suggestedRange={suggestedRange}
              onUseFreeDates={applySuggestedDates}
              occupancyReady={occupancyReady}
              occupancyLoading={occupiedLoad === 'loading'}
              occupancyError={occupiedLoad === 'error'}
              onRetryOccupied={retryOccupiedPlans}
            />
          )}
          {s.sheet === 'leave' && (
            <DiscardSheet
              onDiscard={() => dispatch({ type: 'discard' })}
              onClose={closeSheet}
            />
          )}
          {flashMsg && <div className={css.toast}>{flashMsg}</div>}
          {assessmentDrawer}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default DietPlanBuilder;
